import { Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { bindKey } from '@sama/micro-dash';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { selectionToArray } from '../../../../../cross-team-shared/misc-utils';
import { isPointCloudOutput } from '../../../../../cross-team-shared/misc-utils/output-utils';
import { AbstractAnnotationStore } from '../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { SensorRendererState } from '../../../../../outputs/annotation/annotation-shared/state/root/sensor-renderer-state';
import { removeDynamicTags } from '../../../../../outputs/annotation/annotation-shared/utils/tag-utils';
import { TagService } from '../../../../../outputs/annotation/annotation-shared/utils/tag.service';
import {
  ContentConnector,
  PointCloudContentConnector,
} from '../../../../../team-apis/content/content-connector';
import { AnswerOption } from '../../../../../team-apis/otterdam/answer-option';
import { AnnotationOutput } from '../../../../../team-apis/otterdam/outputs/annotation-output';
import { OutputInfo } from '../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { FeedbackListService } from '../../../../shared/feedback/feedback-list.service';
import { AnnotationStateUndoService } from '../state/root/annotation-state-undo.service';
import { BulkRemoveKeyframesOptions } from '../utils/bulk-remove-frame-annotations.service';
import { InteractionModeService } from '../utils/interaction-mode.service';
import { SelectionService } from '../utils/selection.service';
import { ShapeSelectionService } from '../utils/shape-selection.service';
import {
  ToggleZoomPanelMethod,
  ZoomPanelService,
} from '../zoom-panel/zoom-panel.service';
import { OrthographicOrchestrator } from './projection/orthographic-orchestrator';

export class FusionMinion {
  openTagEditor = bindKey(this.tagService, 'openTagEditor');
  closeTagEditor = bindKey(this.tagService, 'closeTagEditor');
  switchTagEditorTab = bindKey(this.tagService, 'switchTagEditorTab');
  removeKeyframe = bindKey(this.tagService, 'removeKeyframe');

  private orthographicOrchestrator?: OrthographicOrchestrator;

  // eslint-disable-next-line max-params-unless-injectable -- services required
  constructor(
    public output: AnnotationOutput,
    public store: AbstractAnnotationStore,
    public contentConnector: ContentConnector,
    public undoService: AnnotationStateUndoService,
    public shapeSelectionService: ShapeSelectionService,
    public selectionService: SelectionService,
    public injector: Injector,
    public feedbackListService: FeedbackListService,
    private tagService: TagService,
    private taskWorkspaceStore: TaskWorkspaceStore,
    private sensorRendererStore: Store<SensorRendererState>,
    private interactionModeService: InteractionModeService,
    private zoomPanelService: ZoomPanelService,
  ) {}

  isInteractionMode(mode: string) {
    return this.interactionModeService.interactionMode === mode;
  }

  closeZoomPanel(method?: ToggleZoomPanelMethod) {
    this.zoomPanelService.closeZoomPanel(method);
  }

  getOrthographicOrchestrator() {
    assert(
      this.orthographicOrchestrator,
      'Only primary point-cloud outputs have one of these',
    );
    return this.orthographicOrchestrator;
  }

  getPointCloudContentConnector() {
    assert(
      isPointCloudOutput(this.output),
      'Only point-cloud outputs have one of these',
    );
    return this.contentConnector as PointCloudContentConnector;
  }

  getOutputId() {
    return this.output.id;
  }

  hasSingleFrame$() {
    return this.sensorRendererStore('loadedFrames').$.pipe(
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      map((loadedFrames) => loadedFrames?.length === 1),
      distinctUntilChanged(),
    );
  }

  setOrthographicOrchestrator(orchestrator: OrthographicOrchestrator) {
    this.orthographicOrchestrator = orchestrator;
  }

  getShape(index: number) {
    // @ts-ignore -- fix me
    return this.taskWorkspaceStore.state().outputAnnotationInfo[
      this.output.name
    ].shapes[index];
  }

  hasShape(index: number) {
    return !!this.getShape(index);
  }

  removeDynamicTags(
    { startFrameNumber, endFrameNumber }: BulkRemoveKeyframesOptions,
    fusedAnswerOptions: AnswerOption[],
  ) {
    const selected = selectionToArray(
      this.shapeSelectionService.getSelectionForOutput(),
    );
    selected.forEach((index: number) => {
      removeDynamicTags(
        this.taskWorkspaceStore('outputAnnotationInfo')(this.output.name)(
          // @ts-ignore -- fix me
          'shapes',
        )(index),
        startFrameNumber,
        endFrameNumber,
        fusedAnswerOptions,
      );
    });
  }

  /** @deprecated used for old projects without objects */
  updateTag(
    outputInfo: OutputInfo,
    shapeIndex: number,
    frameNumber: number,
    wasOutputVisible: boolean,
  ) {
    this.tagService.updateTag(
      outputInfo,
      shapeIndex,
      frameNumber,
      wasOutputVisible,
      false,
    );
  }
}
